"use client"

import { useState } from "react"
import "./JwtDecoder.css"

function JwtDecoder() {
  const [jwtToken, setJwtToken] = useState("")
  const [decodedJwt, setDecodedJwt] = useState(null)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("header")
  const [copyStatus, setCopyStatus] = useState({})

  const decodeJwt = () => {
    try {
      setError(null)

      if (!jwtToken.trim()) {
        setError("Please enter a JWT token")
        setDecodedJwt(null)
        return
      }

      const parts = jwtToken.split(".")
      if (parts.length !== 3) {
        setError("Invalid JWT format. A JWT consists of three parts separated by dots.")
        setDecodedJwt(null)
        return
      }

      const decoded = {
        header: JSON.parse(atob(parts[0])),
        payload: JSON.parse(atob(parts[1])),
        signature: parts[2],
      }

      setDecodedJwt(decoded)
    } catch (err) {
      setError("Failed to decode JWT. Make sure you entered a valid token.")
      setDecodedJwt(null)
    }
  }

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyStatus({ [section]: true })
      setTimeout(() => {
        setCopyStatus({})
      }, 2000)
    })
  }

  const handleClear = () => {
    setJwtToken("")
    setDecodedJwt(null)
    setError(null)
  }

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2)
  }

  return (
    <div className="jwt-decoder-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">JWT Decoder</h2>
          <p className="card-description">Decode your JWT token to inspect its contents</p>
        </div>
        <div className="card-content">
          <div className="input-group">
            <textarea
              placeholder="Enter your JWT token here..."
              value={jwtToken}
              onChange={(e) => setJwtToken(e.target.value)}
              className="jwt-input"
            />
          </div>
          <div className="button-group">
            <button onClick={decodeJwt} className="decode-button">
              Decode Token
            </button>
            <button onClick={handleClear} className="clear-button">
              Clear
            </button>
          </div>

          {error && (
            <div className="error-alert">
              <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>

      {decodedJwt && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Decoded JWT</h2>
            <p className="card-description">Your JWT token has been successfully decoded</p>
          </div>
          <div className="card-content">
            <div className="tabs">
              <button
                className={`tab-button ${activeTab === "header" ? "active" : ""}`}
                onClick={() => setActiveTab("header")}
              >
                Header
              </button>
              <button
                className={`tab-button ${activeTab === "payload" ? "active" : ""}`}
                onClick={() => setActiveTab("payload")}
              >
                Payload
              </button>
              <button
                className={`tab-button ${activeTab === "signature" ? "active" : ""}`}
                onClick={() => setActiveTab("signature")}
              >
                Signature
              </button>
            </div>

            <div className="tab-content">
              {activeTab === "header" && (
                <div className="code-container">
                  <pre className="code-block">{formatJson(decodedJwt.header)}</pre>
                  <button className="copy-btn" onClick={() => copyToClipboard(formatJson(decodedJwt.header), "header")}>
                    {copyStatus.header ? "Copied!" : "Copy"}
                  </button>
                </div>
              )}

              {activeTab === "payload" && (
                <div className="code-container">
                  <pre className="code-block">{formatJson(decodedJwt.payload)}</pre>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(formatJson(decodedJwt.payload), "payload")}
                  >
                    {copyStatus.payload ? "Copied!" : "Copy"}
                  </button>
                </div>
              )}

              {activeTab === "signature" && (
                <div className="code-container">
                  <pre className="code-block signature">{decodedJwt.signature}</pre>
                  <button className="copy-btn" onClick={() => copyToClipboard(decodedJwt.signature, "signature")}>
                    {copyStatus.signature ? "Copied!" : "Copy"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">About JWT Tokens</h2>
          <p className="card-description">Understanding JSON Web Tokens</p>
        </div>
        <div className="card-content">
          <p className="info-text">
            JSON Web Tokens (JWT) are an open standard (RFC 7519) that defines a compact and self-contained way for
            securely transmitting information between parties as a JSON object.
          </p>

          <div className="info-grid">
            <div className="info-box info-box-blue">
              <h3 className="info-box-title">Structure</h3>
              <p className="info-box-text">
                JWTs consist of three parts separated by dots: Header, Payload, and Signature.
              </p>
            </div>

            <div className="info-box info-box-yellow">
              <h3 className="info-box-title">Security Note</h3>
              <p className="info-box-text">
                Remember that anyone can read the information in a JWT. Do not put secret information in the payload or
                header elements.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default JwtDecoder
